import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { $api } from '../../api/queryClient'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog'
import type { ImportRow } from './importTypes'

interface CreateImportDialogProps {
    org: string
    open: boolean
    onOpenChange: (open: boolean) => void
    /** Called with the freshly created import once the POST succeeded. */
    onCreated?: (created: ImportRow) => void
}

/**
 * "New import" dialog (spec 2026-08-16-05 §1).
 *
 * One optional *Label* field — the backend defaults it to the current date, so
 * submitting straight away is a legitimate one-click "declare a deposit".
 * Shared by the imports list header button and the inline "+ New import" entry
 * of the import selector, so both flows create exactly the same object.
 */
export function CreateImportDialog({ org, open, onOpenChange, onCreated }: CreateImportDialogProps) {
    const { t } = useTranslation('imports')
    const queryClient = useQueryClient()
    const [label, setLabel] = useState('')
    const [error, setError] = useState<string | null>(null)

    // Reset the field every time the dialog opens: a stale label from a
    // previous (possibly cancelled) declaration must never leak into the next.
    useEffect(() => {
        if (open) {
            setLabel('')
            setError(null)
        }
    }, [open])

    const mutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/imports', {
        onSuccess: () => {
            queryClient.invalidateQueries({
                predicate: query => {
                    const queryKey = query.queryKey as unknown[]
                    return queryKey[0] === 'get' && queryKey[1] === '/api/v1/organizations/{orgCode}/imports'
                },
            })
        },
    })

    const submit = async () => {
        setError(null)
        try {
            const created = await mutation.mutateAsync({
                params: { path: { orgCode: org } },
                body: { label: label.trim() || undefined },
            })
            onOpenChange(false)
            onCreated?.(created as unknown as ImportRow)
        } catch {
            setError(t('create.error'))
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="create-import-dialog">
                <DialogHeader>
                    <DialogTitle>{t('create.title')}</DialogTitle>
                    <DialogDescription>{t('create.description')}</DialogDescription>
                </DialogHeader>

                <div className="space-y-2 py-2">
                    <label htmlFor="import-label" className="block text-sm font-medium text-foreground">
                        {t('create.label')}
                    </label>
                    <Input
                        id="import-label"
                        value={label}
                        placeholder={t('create.label_placeholder')}
                        onChange={e => setLabel(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                e.preventDefault()
                                void submit()
                            }
                        }}
                        data-testid="create-import-label-input"
                    />
                    {error && (
                        <p className="text-sm text-destructive" data-testid="create-import-error">
                            {error}
                        </p>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        data-testid="create-import-cancel"
                    >
                        {t('common:actions.cancel')}
                    </Button>
                    <Button
                        onClick={() => void submit()}
                        disabled={mutation.isPending}
                        data-testid="create-import-submit"
                    >
                        {t('create.submit')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
